import { createSlice } from '@reduxjs/toolkit';
import { BrokerConfigurationState } from './state';
import { fetchBrokerAnalizisThunk, fetchBrokerProperties, saveBrokerConfigurationThunk } from './thunks';

export interface BrokerConfigurationSliceState extends BrokerConfigurationState {
  loading: boolean;
  error: string | null;
  showConfiguration: boolean;
  configurations: IConfiguration;
  saving: boolean;
  created: boolean;
  showTables: boolean;
  showEmptyData: boolean;
}

export interface IConfiguration {
  purpose: string;
  type_childrens: string;
  userEmail: string
}

export const initialBrokerConfigurationState: BrokerConfigurationSliceState = {
  brokersProperties: {
    user_id: null,
    total_properties: 0,
    properties_for_sale: 0,
    properties_for_rent: 0,
    properties_by_purpose: [],
    properties_by_type: [],
    sale_price_range: {
      min_price: 0,
      max_price: 0,
    },
    rental_price_range: {
      min_price: 0,
      max_price: 0,
    },
    properties_by_sepomex: [],
    success: false,
  },
  brokersPropertiesFiltered: {
    user_id: null,
    total_properties: 0,
    properties_for_sale: 0,
    properties_for_rent: 0,
    properties_by_purpose: [],
    properties_by_type: [],
    sale_price_range: {
      min_price: 0,
      max_price: 0,
    },
    rental_price_range: {
      min_price: 0,
      max_price: 0,
    },
    properties_by_sepomex: [],
    success: false,
  },
  actualBrokerConfiguration: {
    purpose: "",
    type_childrens: "",
  },
  analysis: [],
  loading: false,
  error: null,
  showConfiguration: false,
  configurations: {
    userEmail: "",
    purpose: "",
    type_childrens: "",
  },
  saving: false,
  created: false,
  showTables: false,
  showEmptyData: false
};

const brokerconfigurationSlice = createSlice({
  name: 'broker_configuration',
  initialState: initialBrokerConfigurationState,
  reducers: {
    setPropertiesTypes: (state, action) => {
      state.configurations.type_childrens = action.payload
    },
    setTransactionType: (state, action) => {
      state.configurations.purpose = action.payload
    },
    setUserEmail: (state, action) => {
      state.configurations.userEmail = action.payload
    },
    setShowCarousels: (state, action) => {
      state.configurations.userEmail = action.payload
    },
    setShowTables: (state, action) => {
      state.showTables = action.payload
    },
    setEmptyValues: (state) => {
      state.brokersPropertiesFiltered = initialBrokerConfigurationState.brokersPropertiesFiltered
    },
    resetValues: (state) => {
      state.showTables = false
      state.configurations = { 
        ...initialBrokerConfigurationState.configurations, 
        userEmail: state.configurations.userEmail 
      }
      state.actualBrokerConfiguration = initialBrokerConfigurationState.actualBrokerConfiguration
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBrokerProperties.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.showEmptyData = false;
      })
      .addCase(fetchBrokerProperties.fulfilled, (state, action) => {
        if ((action.payload?.error && action.payload?.error !== 'AbortError')
          || action.payload.brokersProperties?.properties_by_type?.length === 0) {
          state.showEmptyData = true;
          state.showTables = false;
          state.loading = false;
          state.showConfiguration = false;
          return
        }
        if (action.payload.filter) {
          if (state.configurations.type_childrens === "" && state.configurations.purpose === "") {
            state.brokersPropertiesFiltered = initialBrokerConfigurationState.brokersPropertiesFiltered
          } else {
            Object.assign(state.brokersPropertiesFiltered, action.payload.brokersPropertiesFiltered);
          }
        }
        if (!action.payload.filter) {
          Object.assign(state.brokersPropertiesFiltered, action.payload.brokersPropertiesFiltered);
          Object.assign(state.brokersProperties, action.payload.brokersProperties);
          Object.assign(state.configurations, action.payload.actualBrokerConfiguration)
          Object.assign(state.actualBrokerConfiguration, action.payload.actualBrokerConfiguration);
        }
        state.showTables = true;
        state.loading = action.payload?.error === 'AbortError';
        state.error = null;
        state.showConfiguration = true;
      })
      .addCase(fetchBrokerProperties.rejected, (state, action) => {
        state.loading = false;
        state.showConfiguration = false;
        state.showTables = false;
        state.error = action.error.message || 'Error al obtener propiedades del broker';
      })
      .addCase(saveBrokerConfigurationThunk.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.created = false;
      })
      .addCase(saveBrokerConfigurationThunk.fulfilled, (state, action) => {
        state.saving = false;
        state.created = true;
        state.error = null;
        if (action.payload) {
          state.actualBrokerConfiguration = {
            purpose: state.configurations.purpose,
            type_childrens: state.configurations.type_childrens,
          };
        }
      })
      .addCase(saveBrokerConfigurationThunk.rejected, (state, action) => {
        state.saving = false;
        state.created = false;
        state.error = action.error.message || 'Error al guardar la configuración del broker';
      })
      .addCase(fetchBrokerAnalizisThunk.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.created = false;
      })
      .addCase(fetchBrokerAnalizisThunk.fulfilled, (state, action) => {
        state.saving = false;
        state.created = true;
        state.error = null;
        if (action.payload) {
          state.analysis = action.payload.analysis;
        }
      })
      .addCase(fetchBrokerAnalizisThunk.rejected, (state, action) => {
        state.saving = false;
        state.created = false;
        state.error = action.error.message || 'Error al guardar la configuración del broker';
      });
  }
});

export const {
  setPropertiesTypes,
  setTransactionType,
  setUserEmail,
  setShowCarousels,
  setShowTables,
  setEmptyValues,
  resetValues
} = brokerconfigurationSlice.actions;


export default brokerconfigurationSlice.reducer;
